import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

import { ReactComponent as AttachmentIcon } from "assets/img/attachment-icon.svg";
import { ReactComponent as PdfIcon } from "assets/img/pdf-icon.svg";
import { ReactComponent as NoneIcon } from "assets/img/none-icon.svg";
import { getFileType } from "components/Carousel/getFileType";
import { Image } from "components/Carousel/Image";
import { AttachmentDetail } from "interfaces/attachment";
import { useUnmount } from "react-use";
import { downloadAsDataUri } from "core/utils/aws/downloadAsDataUri";
import { useState } from "react";

interface CarouselContentProps {
  hasAttachments: boolean;
  attachment: AttachmentDetail | undefined;
}

export const CarouselContent = ({
  hasAttachments = false,
  attachment
}: CarouselContentProps): JSX.Element => {
  const mimeType: string = attachment?.original_file.type ?? "";
  const title: string = attachment?.original_file.name ?? "";
  const controller = new AbortController();
  const [src, setSrc] = useState("");
  useUnmount(() => {
    controller.abort();
  });

  if (!hasAttachments || !attachment || !mimeType) {
    return (
      <StyledBox aria-label={title}>
        <NoneIcon data-testid="carousel-none" />
      </StyledBox>
    );
  }

  switch (getFileType(mimeType)) {
    case "image":
      (async () => {
        const url = attachment?.thumbnail_medium?.url
          ? attachment?.thumbnail_medium.url
          : attachment?.original_file.url ?? "";

        const headers = attachment?.thumbnail_medium?.download_headers
          ? attachment?.thumbnail_medium.download_headers
          : attachment?.original_file.download_headers;

        setSrc(
          await downloadAsDataUri({
            url,
            headers,
            signal: controller.signal
          })
        );
      })();

      return (
        <Image
          data-testid="carousel-image"
          src={src}
          alt={attachment?.original_file.name ?? ""}
        />
      );

    case "pdf":
      return (
        <StyledBox aria-label={title}>
          <PdfIcon data-testid="carousel-pdf" />
        </StyledBox>
      );

    default:
      return (
        <StyledBox aria-label={title}>
          <AttachmentIcon data-testid="carousel-unknown" />
        </StyledBox>
      );
  }
};

const StyledBox = styled(Box)`
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
